import { Menu } from '@ark-ui/react/menu'
import { Portal } from '@ark-ui/react/portal'
import { Link } from '@tanstack/react-router'
import clsx from 'clsx'
import { Icon } from 'grafbase-components/ui/icon/icon'
import { type Theme, useTheme } from 'grafbase-styles'
import { graphql, useFragment } from 'react-relay'
import type { profileMenuFragment$key } from '#gen/relay/profileMenuFragment.graphql'
import { signOut } from '#lib/auth/sign-out'
import { Avatar } from '#lib/ui/avatar/avatar'
import { Pill } from '#lib/ui/pill/pill'
import { profileMenuStyles as styles } from './profile-menu.css'

const query = graphql`
  fragment profileMenuFragment on User {
    name
    email
    avatarUrl
  }
`

export function ProfileMenu(props: {
  dataRef: profileMenuFragment$key
  triggerClassName?: string
}) {
  const user = useFragment(query, props.dataRef)
  const [{ theme }, setTheme] = useTheme()

  return (
    <Menu.Root
      aria-label="Profile menu"
      onSelect={({ value }) => {
        if (value === 'sign-out') {
          signOut()
        }
      }}
    >
      <Menu.Trigger className={clsx(props.triggerClassName, styles.trigger)}>
        <Avatar size="small" src={user.avatarUrl} name={user.name} />
        <div className={styles.triggerInfo}>
          <p className={styles.triggerName}>{user.name}</p>
          <p className={styles.triggerEmail}>{user.email}</p>
        </div>
      </Menu.Trigger>

      <Menu.Positioner>
        <Menu.Content className={styles.content}>
          <div className={styles.infoItem}>
            <div>{user.name}</div>
            <div className={styles.email}>{user.email}</div>
          </div>

          <Menu.Separator className={styles.separator} />

          <Menu.Item value="settings" asChild className={styles.item}>
            <Link to="/settings">Settings</Link>
          </Menu.Item>

          <Menu.Root
            positioning={{ placement: 'right', gutter: -2 }}
            onSelect={async ({ value }) => {
              const theme = value as Theme
              setTheme(theme)
            }}
          >
            <Menu.TriggerItem className={styles.triggerItem}>
              Theme
              <span className={styles.triggerItemIndicator}>
                <Pill color="neutral">{theme}</Pill>
                <Icon icon={'chevron-right'} size="md" />
              </span>
            </Menu.TriggerItem>

            <Portal>
              <Menu.Positioner>
                <Menu.Content className={styles.subMenuContent}>
                  <div className={styles.subMenuContentInner}>
                    <ThemeItem targetTheme="system" />
                    <ThemeItem targetTheme="dark" />
                    <ThemeItem targetTheme="light" />
                  </div>
                </Menu.Content>
              </Menu.Positioner>
            </Portal>
          </Menu.Root>

          <Menu.Separator className={styles.separator} />

          <Menu.Item value="homepage" asChild className={styles.item}>
            <a
              href="https://grafbase.com/"
              target="_blank"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              Homepage
              <Icon icon="external-link" size="sm" />
            </a>
          </Menu.Item>

          <Menu.Separator className={styles.separator} />

          <Menu.Item value="sign-out" className={styles.item}>
            Sign Out
          </Menu.Item>
        </Menu.Content>
      </Menu.Positioner>
    </Menu.Root>
  )
}

interface ThemeItemProps {
  targetTheme: Theme
}

function ThemeItem({ targetTheme }: ThemeItemProps) {
  const [{ theme }] = useTheme()

  return (
    <Menu.Item value={targetTheme} className={styles.subMenuItem}>
      <span className={styles.subMenuItemLabel}>{targetTheme}</span>

      <Icon
        icon={'check'}
        size="xs"
        aria-label="Checked"
        aria-hidden={theme !== targetTheme}
        className={styles.itemCheckedIndicator}
      />
    </Menu.Item>
  )
}
